import React, { useEffect, useMemo, useState } from "react";
import { listClasses, listRooms, saveRooms, getRoomAssignment, saveRoomAssignment, listTimetableWeeks, DAYS } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";
import { ROOM_TYPES, ROOM_TYPE_BY_KEY, roomLabel, indexByClass } from "./roomAssignment";

// (04/11/2026) "Phân phòng học" của 1 tuần TKB — giống bảng "PHÂN PHÒNG HỌC CHO CÁC LỚP": hàng = Thứ × Buổi,
// cột = phòng (nhóm theo toà nhà), ô = lớp học ở phòng đó, tô màu theo loại Nghề / Chính khoá / Tăng cường.
// 1 lớp có thể ở 2 phòng cùng buổi (lớp nghề chia nhóm) — viền cam để dễ soát.
// canEdit: owner/admin/BGH bấm ô để xếp/đổi/xoá lớp, sửa danh sách phòng, rồi bấm "Lưu".
const SESSIONS = ["Sáng", "Chiều"];
const SLOTS = DAYS.flatMap((d, day) => SESSIONS.map((s, si) => ({ day, si, dayLabel: d, sesLabel: s })));
const fmtTime = (ts) => (ts ? new Date(ts).toLocaleString("vi-VN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : "");

const HappySchoolRoomAssignment = ({ weekId, canEdit = false }) => {
  const schoolId = getActiveSchoolId();
  const [tab, setTab] = useState("room"); // room | class | free | list
  const [classes, setClasses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [cells, setCells] = useState({}); // bản đang sửa
  const [savedCells, setSavedCells] = useState({}); // bản đã lưu trên máy chủ
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null); // { roomId, day, si } | { classId, day, si }
  const [freeSlot, setFreeSlot] = useState("0|0");
  const [weeks, setWeeks] = useState([]);

  useEffect(() => {
    if (!schoolId || !weekId) return;
    setLoading(true);
    setError("");
    Promise.all([listClasses(schoolId), listRooms(schoolId), getRoomAssignment(schoolId, weekId)])
      .then(([c, r, a]) => {
        // Lớp giải tán vẫn hiện (vẫn có buổi học nghề) — gắn nhãn "(giải tán)".
        setClasses((c || []).sort((x, y) => x.name.localeCompare(y.name, "vi", { numeric: true })));
        setRooms(r || []);
        setCells((a && a.cells) || {});
        setSavedCells((a && a.cells) || {});
        setUpdatedAt(a && a.updatedAt);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    if (canEdit) listTimetableWeeks(schoolId).then((w) => setWeeks((w || []).filter((x) => x.id !== weekId).sort((a, b) => String(b.startDate).localeCompare(String(a.startDate))))).catch(() => {});
  }, [schoolId, weekId, canEdit]);

  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c])), [classes]);
  const roomById = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);
  const dirty = JSON.stringify(cells) !== JSON.stringify(savedCells);

  // "{classId}|{day}|{si}" -> [{ roomId, type }]
  const byClass = useMemo(() => indexByClass(cells, rooms), [cells, rooms]);
  const multiRoomCount = Object.values(byClass).filter((a) => a.length > 1).length;

  // Toà nhà theo thứ tự xuất hiện trong danh sách phòng.
  const buildings = useMemo(() => {
    const out = [];
    rooms.forEach((r) => {
      const b = r.building || "Khác";
      let g = out.find((x) => x.name === b);
      if (!g) out.push((g = { name: b, rooms: [] }));
      g.rooms.push(r);
    });
    return out;
  }, [rooms]);

  const qn = q.trim().toLowerCase();
  const matchClass = (classId) => !qn || ((classById[classId] || {}).name || "").toLowerCase().includes(qn);
  // Tab "Theo lớp": lớp giải tán chỉ hiện khi tuần này có xếp phòng cho lớp đó.
  const classIdsWithRoom = useMemo(() => new Set(Object.values(cells).map((c) => c.classId)), [cells]);
  const shownClasses = classes.filter((c) => matchClass(c.id) && (c.status !== "giai_tan" || classIdsWithRoom.has(c.id)));

  const setCell = (roomId, day, si, val) => {
    setCells((prev) => {
      const next = { ...prev };
      const k = `${roomId}|${day}|${si}`;
      if (val) next[k] = val;
      else delete next[k];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveRoomAssignment(schoolId, weekId, cells);
      const a = await getRoomAssignment(schoolId, weekId);
      setCells(a.cells || {});
      setSavedCells(a.cells || {});
      setUpdatedAt(a.updatedAt);
    } catch (e) {
      window.alert(e.message || "Lưu không được.");
    } finally {
      setSaving(false);
    }
  };

  const copyFromWeek = async (srcWeekId) => {
    if (!srcWeekId) return;
    const a = await getRoomAssignment(schoolId, srcWeekId).catch((e) => { window.alert(e.message); return null; });
    if (!a) return;
    const n = Object.keys(a.cells || {}).length;
    if (!n) { window.alert("Tuần đó chưa có phân phòng."); return; }
    if (!window.confirm(`Thay bảng phân phòng hiện tại bằng ${n} ô của tuần đã chọn? (Chưa lưu cho tới khi bấm "Lưu")`)) return;
    setCells(a.cells);
  };

  if (!weekId) return <p style={{ color: "#6b7280" }}>Chưa chọn tuần TKB.</p>;
  if (loading) return <p>Đang tải...</p>;
  if (error) return <p style={{ color: "#dc2626" }}>{error}</p>;

  const tabs = [["room", "🚪 Theo phòng"], ["class", "🎒 Theo lớp"], ["free", "🟢 Phòng trống"], ...(canEdit ? [["list", "📋 Danh sách phòng"]] : [])];
  const typeChip = (type, text, extra = {}) => {
    const t = ROOM_TYPE_BY_KEY[type] || ROOM_TYPE_BY_KEY.chinhkhoa;
    return <span style={{ display: "inline-block", background: t.bg, color: t.fg, borderRadius: 4, padding: "1px 5px", fontWeight: 700, fontSize: 11, ...extra }}>{text}</span>;
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
        {tabs.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} style={tab === k ? tabOn : tabBtn}>{l}</button>
        ))}
        {tab !== "list" && tab !== "free" && <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lọc theo lớp (vd 11A1)" style={inputStyle} />}
        <span style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: "#6b7280" }}>
          {ROOM_TYPES.map((t) => <span key={t.key}>{typeChip(t.key, t.label)}</span>)}
        </span>
      </div>

      {tab !== "list" && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10, fontSize: 13, color: "#475569" }}>
          <span>{Object.keys(cells).length} ô phòng-buổi đã xếp lớp · {rooms.length} phòng</span>
          {multiRoomCount > 0 && <span style={{ color: "#b45309", fontWeight: 600 }}>ℹ️ {multiRoomCount} buổi có lớp học ở 2 phòng (viền cam)</span>}
          {updatedAt && <span>· Cập nhật {fmtTime(updatedAt)}</span>}
          {canEdit && (
            <>
              <select value="" onChange={(e) => copyFromWeek(e.target.value)} style={{ ...inputStyle, minWidth: 0 }}>
                <option value="">Sao chép từ tuần khác…</option>
                {weeks.map((w) => <option key={w.id} value={w.id}>{w.name || `Tuần ${w.startDate} – ${w.endDate}`}</option>)}
              </select>
              <button type="button" disabled={!dirty || saving} onClick={save} style={dirty ? saveBtn : { ...saveBtn, opacity: 0.5, cursor: "default" }}>{saving ? "Đang lưu..." : "💾 Lưu phân phòng"}</button>
              {dirty && <button type="button" onClick={() => setCells(savedCells)} style={tabBtn}>Huỷ thay đổi</button>}
            </>
          )}
        </div>
      )}
      {canEdit && tab !== "list" && rooms.length > 0 && <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px" }}>Bấm vào 1 ô để xếp / đổi / xoá lớp.</p>}

      {tab !== "list" && rooms.length === 0 && (
        <p style={{ color: "#6b7280" }}>Trường chưa có danh sách phòng học{canEdit ? " — vào tab “📋 Danh sách phòng” để thêm." : "."}</p>
      )}

      {tab === "room" && rooms.length > 0 && (
        <div style={{ overflow: "auto", maxHeight: "75vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...thFix(0), minWidth: 54, zIndex: 7 }}>Thứ</th>
                <th rowSpan={2} style={{ ...thFix(54), minWidth: 50, zIndex: 7 }}>Buổi</th>
                {buildings.map((b) => <th key={b.name} colSpan={b.rooms.length} style={{ ...thTop(0), background: "#dbeafe" }}>Nhà {b.name}</th>)}
              </tr>
              <tr>
                {buildings.flatMap((b) => b.rooms.map((r) => (
                  <th key={r.id} title={[r.note, r.status].filter(Boolean).join(" · ")} style={{ ...thTop(25), minWidth: 52 }}>{r.name}{r.note ? " *" : ""}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {SLOTS.map(({ day, si, dayLabel, sesLabel }) => (
                <tr key={`${day}-${si}`} style={si === 0 ? { borderTop: "2px solid #94a3b8" } : undefined}>
                  {si === 0 && <td rowSpan={2} style={{ ...tdFix(0), fontWeight: 700, background: "#eff6ff" }}>{dayLabel.replace("Thứ ", "")}</td>}
                  <td style={{ ...tdFix(54) }}>{sesLabel}</td>
                  {buildings.flatMap((b) => b.rooms.map((r) => {
                    const c = cells[`${r.id}|${day}|${si}`];
                    const multi = c && (byClass[`${c.classId}|${day}|${si}`] || []).length > 1;
                    return (
                      <td key={r.id} onClick={canEdit ? () => setTarget({ roomId: r.id, day, si }) : undefined}
                        style={{ ...td, cursor: canEdit ? "pointer" : undefined, boxShadow: multi ? "inset 0 0 0 2px #f59e0b" : undefined }}>
                        {c && matchClass(c.classId) ? typeChip(c.type, (classById[c.classId] || {}).name || c.classId) : ""}
                      </td>
                    );
                  }))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "class" && rooms.length > 0 && (
        <div style={{ overflow: "auto", maxHeight: "75vh", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ ...thFix(0), minWidth: 64, zIndex: 7 }}>Lớp</th>
                {DAYS.map((d) => <th key={d} colSpan={2} style={{ ...thTop(0), background: "#dbeafe" }}>{d}</th>)}
              </tr>
              <tr>{SLOTS.map((s) => <th key={`${s.day}-${s.si}`} style={{ ...thTop(25), minWidth: 74 }}>{s.sesLabel}</th>)}</tr>
            </thead>
            <tbody>
              {shownClasses.map((c) => (
                <tr key={c.id}>
                  <td style={{ ...tdFix(0), fontWeight: 700 }}>{c.name}{c.status === "giai_tan" && <div style={{ fontWeight: 400, fontSize: 10, color: "#9ca3af" }}>giải tán</div>}</td>
                  {SLOTS.map(({ day, si }) => {
                    const list = byClass[`${c.id}|${day}|${si}`] || [];
                    return (
                      <td key={`${day}-${si}`} onClick={canEdit ? () => setTarget({ classId: c.id, day, si }) : undefined}
                        style={{ ...td, cursor: canEdit ? "pointer" : undefined, boxShadow: list.length > 1 ? "inset 0 0 0 2px #f59e0b" : undefined, borderLeft: si === 0 ? "2px solid #94a3b8" : td.border }}>
                        {list.map((x) => <div key={x.roomId}>{typeChip(x.type, roomLabel(roomById[x.roomId]) || "?")}</div>)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "free" && rooms.length > 0 && (() => {
        const [d, s] = freeSlot.split("|").map(Number);
        const free = rooms.filter((r) => !cells[`${r.id}|${d}|${s}`]);
        return (
          <div>
            <select value={freeSlot} onChange={(e) => setFreeSlot(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }}>
              {SLOTS.map((x) => <option key={`${x.day}|${x.si}`} value={`${x.day}|${x.si}`}>{x.dayLabel} — {x.sesLabel}</option>)}
            </select>
            <p style={{ fontSize: 13, color: "#475569", margin: "0 0 8px" }}>{free.length}/{rooms.length} phòng trống {DAYS[d]} buổi {SESSIONS[s].toLowerCase()}:</p>
            {buildings.map((b) => {
              const list = free.filter((r) => (r.building || "Khác") === b.name);
              if (!list.length) return null;
              return (
                <div key={b.name} style={{ marginBottom: 8 }}>
                  <b style={{ fontSize: 13 }}>Nhà {b.name}:</b>{" "}
                  {list.map((r) => <span key={r.id} title={r.note || ""} style={freeChip}>{r.name}{r.note ? ` · ${r.note}` : ""}</span>)}
                </div>
              );
            })}
          </div>
        );
      })()}

      {tab === "list" && canEdit && <RoomListEditor schoolId={schoolId} rooms={rooms} usedRoomIds={new Set(Object.keys(cells).map((k) => k.split("|")[0]))} onSaved={setRooms} />}

      {target && (
        <CellDialog target={target} cells={cells} byClass={byClass} classes={classes} classById={classById} rooms={rooms} roomById={roomById}
          setCell={setCell} onClose={() => setTarget(null)} />
      )}
    </div>
  );
};

// Hộp sửa 1 ô: theo phòng (roomId) -> chọn lớp học ở phòng đó; theo lớp (classId) -> các phòng của lớp buổi đó.
function CellDialog({ target, cells, byClass, classes, classById, rooms, roomById, setCell, onClose }) {
  const { day, si } = target;
  const [addRoomId, setAddRoomId] = useState("");
  const slotName = `${DAYS[day]} — ${SESSIONS[si]}`;
  const roomsOfClass = (classId) => byClass[`${classId}|${day}|${si}`] || [];
  const typeSelect = (value, onChange) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
      {ROOM_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
    </select>
  );

  let body;
  if (target.roomId) {
    const cur = cells[`${target.roomId}|${day}|${si}`];
    body = (
      <>
        <h3 style={{ margin: "0 0 4px" }}>Phòng {roomLabel(roomById[target.roomId])}</h3>
        <p style={{ margin: "0 0 12px", color: "#6b7280", fontSize: 13 }}>{slotName}</p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={cur ? cur.classId : ""} onChange={(e) => setCell(target.roomId, day, si, e.target.value ? { classId: e.target.value, type: cur ? cur.type : "chinhkhoa" } : null)} style={inputStyle}>
            <option value="">— Phòng trống —</option>
            {classes.map((c) => {
              const other = roomsOfClass(c.id).filter((x) => x.roomId !== target.roomId);
              return <option key={c.id} value={c.id}>{c.name}{c.status === "giai_tan" ? " (giải tán)" : ""}{other.length ? ` (đang ở ${other.map((x) => roomLabel(roomById[x.roomId])).join(", ")})` : ""}</option>;
            })}
          </select>
          {cur && typeSelect(cur.type, (t) => setCell(target.roomId, day, si, { ...cur, type: t }))}
        </div>
        {cur && roomsOfClass(cur.classId).length > 1 && <p style={{ fontSize: 12, color: "#b45309", margin: "8px 0 0" }}>Lớp {(classById[cur.classId] || {}).name} đang ở {roomsOfClass(cur.classId).length} phòng buổi này (chia nhóm?) — nếu là chuyển phòng, hãy bỏ lớp khỏi phòng cũ.</p>}
      </>
    );
  } else {
    const list = roomsOfClass(target.classId);
    body = (
      <>
        <h3 style={{ margin: "0 0 4px" }}>Lớp {(classById[target.classId] || {}).name}</h3>
        <p style={{ margin: "0 0 12px", color: "#6b7280", fontSize: 13 }}>{slotName}</p>
        {list.length === 0 && <p style={{ fontSize: 13, color: "#6b7280" }}>Chưa xếp phòng.</p>}
        {list.map((x) => (
          <div key={x.roomId} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
            <b style={{ minWidth: 90 }}>{roomLabel(roomById[x.roomId])}</b>
            {typeSelect(x.type, (t) => setCell(x.roomId, day, si, { classId: target.classId, type: t }))}
            <button type="button" onClick={() => setCell(x.roomId, day, si, null)} style={delBtn}>Bỏ phòng này</button>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <select value={addRoomId} onChange={(e) => setAddRoomId(e.target.value)} style={inputStyle}>
            <option value="">{list.length ? "+ Thêm phòng (chia nhóm)…" : "+ Chọn phòng…"}</option>
            {rooms.filter((r) => !list.some((x) => x.roomId === r.id)).map((r) => {
              const occ = cells[`${r.id}|${day}|${si}`];
              return <option key={r.id} value={r.id}>{roomLabel(r)}{occ ? ` — đang có ${(classById[occ.classId] || {}).name || "?"}` : " — trống"}</option>;
            })}
          </select>
          <button type="button" disabled={!addRoomId} onClick={() => {
            const occ = cells[`${addRoomId}|${day}|${si}`];
            if (occ && !window.confirm(`Phòng ${roomLabel(roomById[addRoomId])} đang có lớp ${(classById[occ.classId] || {}).name}. Thay bằng lớp này?`)) return;
            setCell(addRoomId, day, si, { classId: target.classId, type: list[0] ? list[0].type : "chinhkhoa" });
            setAddRoomId("");
          }} style={saveBtn}>Xếp</button>
        </div>
      </>
    );
  }
  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        {body}
        <div style={{ textAlign: "right", marginTop: 16 }}>
          <button type="button" onClick={onClose} style={tabBtn}>Xong</button>
        </div>
        <p style={{ fontSize: 11, color: "#9ca3af", margin: "8px 0 0" }}>Thay đổi chỉ được ghi khi bấm “💾 Lưu phân phòng”.</p>
      </div>
    </div>
  );
}

// Sửa danh sách phòng của trường (dùng chung mọi tuần). Dán được từ Excel: Phòng ⇥ Toà nhà ⇥ Ghi chú ⇥ Tình trạng.
function RoomListEditor({ schoolId, rooms, usedRoomIds, onSaved }) {
  const [rows, setRows] = useState(() => rooms.map((r) => ({ ...r })));
  const [paste, setPaste] = useState("");
  const [saving, setSaving] = useState(false);
  const upd = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const move = (i, d) => setRows((rs) => { const n = rs.slice(); const j = i + d; if (j < 0 || j >= n.length) return rs; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const addPasted = () => {
    const add = paste.split(/\r?\n/).map((l) => l.split("\t").map((x) => x.trim())).filter((c) => c[0])
      .map(([name, building = "", note = "", status = ""]) => ({ name, building, note, status }));
    setRows((rs) => [...rs, ...add]);
    setPaste("");
  };
  const save = async () => {
    setSaving(true);
    try { const r = await saveRooms(schoolId, rows); onSaved(r); setRows(r.map((x) => ({ ...x }))); window.alert("Đã lưu danh sách phòng."); }
    catch (e) { window.alert(e.message || "Lưu không được."); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <p style={{ fontSize: 13, color: "#475569", margin: "0 0 8px" }}>Danh sách phòng dùng chung cho mọi tuần. Thứ tự ở đây = thứ tự cột trên bảng phân phòng.</p>
      <table style={{ borderCollapse: "collapse", fontSize: 13, marginBottom: 10 }}>
        <thead><tr>{["#", "Phòng", "Toà nhà", "Ghi chú", "Tình trạng", ""].map((h) => <th key={h} style={{ ...thTop(0), position: "static" }}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || `new-${i}`}>
              <td style={td}>{i + 1}</td>
              <td style={td}><input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} style={{ ...cellInput, width: 80 }} /></td>
              <td style={td}><input value={r.building || ""} onChange={(e) => upd(i, "building", e.target.value)} style={{ ...cellInput, width: 60 }} /></td>
              <td style={td}><input value={r.note || ""} onChange={(e) => upd(i, "note", e.target.value)} style={{ ...cellInput, width: 260 }} /></td>
              <td style={td}><input value={r.status || ""} onChange={(e) => upd(i, "status", e.target.value)} style={{ ...cellInput, width: 110 }} /></td>
              <td style={{ ...td, whiteSpace: "nowrap" }}>
                <button type="button" onClick={() => move(i, -1)} style={miniBtn}>↑</button>
                <button type="button" onClick={() => move(i, 1)} style={miniBtn}>↓</button>
                <button type="button" onClick={() => {
                  if (r.id && usedRoomIds.has(r.id) && !window.confirm(`Phòng ${r.name} đang được xếp lớp trong tuần này. Vẫn xoá?`)) return;
                  setRows((rs) => rs.filter((_, j) => j !== i));
                }} style={{ ...miniBtn, color: "#dc2626" }}>✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 10 }}>
        <button type="button" onClick={() => setRows((rs) => [...rs, { name: "", building: "", note: "", status: "Đang sử dụng" }])} style={tabBtn}>+ Thêm phòng</button>
        <textarea value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={"Dán từ Excel (mỗi dòng: Phòng ⇥ Toà nhà ⇥ Ghi chú ⇥ Tình trạng)"} rows={3} style={{ ...inputStyle, minWidth: 360, fontFamily: "inherit" }} />
        <button type="button" disabled={!paste.trim()} onClick={addPasted} style={tabBtn}>Thêm từ ô dán</button>
      </div>
      <button type="button" disabled={saving} onClick={save} style={saveBtn}>{saving ? "Đang lưu..." : "💾 Lưu danh sách phòng"}</button>
    </div>
  );
}

const tabBtn = { padding: "6px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 999, cursor: "pointer", fontWeight: 600, fontSize: 12 };
const tabOn = { ...tabBtn, background: "#0ea5a3", color: "#fff", border: "1px solid #0ea5a3" };
const saveBtn = { padding: "6px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const delBtn = { padding: "4px 10px", background: "#fff", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer", fontSize: 12 };
const miniBtn = { padding: "1px 6px", background: "#fff", border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 2 };
const inputStyle = { padding: "6px 8px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, minWidth: 160 };
const cellInput = { padding: "3px 5px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 13 };
const freeChip = { display: "inline-block", margin: "2px 4px 2px 0", padding: "2px 8px", background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", borderRadius: 10, fontSize: 12 };
const th = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f9fafb", whiteSpace: "nowrap", textAlign: "center" };
const thTop = (top) => ({ ...th, position: "sticky", top, zIndex: 3 });
const thFix = (left) => ({ ...th, position: "sticky", left, top: 0, zIndex: 4, background: "#f3f4f6" });
const td = { border: "1px solid #e5e7eb", padding: "2px 3px", textAlign: "center", verticalAlign: "middle", height: 22 };
const tdFix = (left) => ({ ...td, position: "sticky", left, zIndex: 2, background: "#fff", whiteSpace: "nowrap" });
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const dialog = { background: "#fff", borderRadius: 12, padding: 20, width: "min(520px, 100%)", maxHeight: "85vh", overflow: "auto", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" };

export default HappySchoolRoomAssignment;
